/**
 * PATH: packages/job-hub/src/jobs-list/gateway/platformJobsQuoteAttach.ts
 * FILE_ID: JOBHUB-JOBSLIST-QUOTEATTACH-001
 * PURPOSE: People roster, deterministic quote-sheet upload, and QDrive fetch.
 * WHY: Split off the gateway factory so it stays under the 500-line ceiling
 *   after the create-from-file door grew three new transports.
 * FLOW: people() -> Employee ids; uploadQuoteSheet(job, file, allowAi=false)
 *   -> QuoteSheet; qdrive.list/download -> File for the same upload path.
 */
import type {
  JobsListOutcome,
  QDriveFileSummary,
  QuoteSheetUploadResult,
  TeamRoleOption,
} from './JobsListGateway'

type HttpResponse<T> = { data: T; status?: number; headers?: Record<string, string> }
type JobsListHttpClient = {
  get<T>(url: string, config?: { params?: Record<string, unknown>; responseType?: string }): Promise<HttpResponse<T>>
  post<T>(url: string, data?: unknown, config?: { params?: Record<string, unknown>; responseType?: string; headers?: Record<string, string> }): Promise<HttpResponse<T>>
}

/**
 * Normalise a QDrive list or search payload into picker rows.
 *
 * Inputs: whatever `/api/qdrive/files/` or `/api/qdrive/search/` returned.
 * Output: rows carrying an id and a filename; anything else is dropped.
 * Search hits nest the record under `file`; the list endpoint returns it flat.
 */
export function qdriveRows(payload: unknown): QDriveFileSummary[] {
  const envelope = payload as { results?: unknown; files?: unknown } | null
  const raw = Array.isArray(payload)
    ? payload
    : (Array.isArray(envelope?.results) ? envelope.results
      : Array.isArray(envelope?.files) ? envelope.files : [])
  return (raw as Record<string, unknown>[]).flatMap((row) => {
    const src = (row.file && typeof row.file === 'object' ? row.file : row) as Record<string, unknown>
    const id = src.id ?? src.file_id
    const filename = src.filename ?? src.original_filename ?? src.name
    if (id == null || !filename) return []
    return [{
      id: String(id),
      filename: String(filename),
      content_type: (src.content_type ?? src.mime_type ?? null) as string | null,
      file_size: typeof src.file_size === 'number' ? src.file_size : null,
      uploaded_at: (src.uploaded_at ?? src.created_at ?? null) as string | null,
      folder_path: (src.folder_path ?? src.path ?? null) as string | null,
    }]
  })
}

export function quoteAttachMethods({
  apiClient,
  scope,
  createActionHeaders,
  outcome,
}: {
  apiClient: JobsListHttpClient
  scope: Record<string, unknown>
  createActionHeaders: () => Record<string, string>
  outcome: <T>(request: Promise<HttpResponse<T>>) => Promise<JobsListOutcome<T>>
}) {
  return {
    people: (): Promise<TeamRoleOption[]> =>
      apiClient.get<unknown>('/api/job-schedule/people/', { params: scope }).then(({ data }) => {
        const rows = Array.isArray(data) ? data : ((data as { results?: unknown[] })?.results ?? [])
        return (rows as Array<Record<string, unknown>>)
          .map((row) => ({
            id: Number(row.id),
            name: String(row.full_name ?? row.name ?? '').trim(),
          }))
          .filter((row) => Number.isFinite(row.id) && row.id > 0 && row.name)
      }),
    uploadQuoteSheet: (
      jobId: number,
      file: File,
      options?: { allowAi?: boolean; mainQuote?: boolean },
    ) => {
      const body = new FormData()
      body.append('file', file)
      body.append('main_quote', options?.mainQuote ? 'true' : 'false')
      // Explicit on both branches: the endpoint's default is AI-on, and the
      // create-from-a-file flow must never silently fall back into it.
      body.append('allow_ai', options?.allowAi === false ? 'false' : 'true')
      return outcome(apiClient.post<QuoteSheetUploadResult>(
        `/api/v1/jobs/${jobId}/quote-sheets/upload/`,
        body,
        { params: scope, headers: createActionHeaders() },
      ))
    },
    qdrive: {
      list: async (query?: string) => {
        const q = query?.trim()
        const { data } = q
          ? await apiClient.get<unknown>('/api/qdrive/search/', { params: { ...scope, q } })
          : await apiClient.get<unknown>('/api/qdrive/files/', {
            params: { ...scope, page_size: 50, ordering: '-uploaded_at' },
          })
        return qdriveRows(data)
      },
      download: async (file: QDriveFileSummary) => {
        const response = await apiClient.get<Blob>(
          `/api/qdrive/files/${file.id}/download/`,
          { params: scope, responseType: 'blob' },
        )
        let blob = response.data
        if (String(response.headers?.['content-type'] ?? '').includes('application/json')) {
          const payload = JSON.parse(await blob.text()) as { url?: string | null }
          if (!payload.url) throw new Error('QDrive did not return a download URL.')
          const signed = await fetch(payload.url)
          if (!signed.ok) throw new Error('Could not download the selected QDrive file.')
          blob = await signed.blob()
        }
        return new File([blob], file.filename, {
          type: blob.type || file.content_type || undefined,
        })
      },
    },
  }
}
